import {
  useEffect,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  CornerLeftUp,
  FolderInput,
  FolderPlus,
  ListTodo,
  ListTree,
  MoreHorizontal,
  PanelRight,
  Pencil,
  Plus,
  Trash2,
  Unlink,
} from 'lucide-react';
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
} from '@/components/ui/BottomSheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  ActionSheet,
  ActionSheetItem,
  OptionSheet,
  SheetChip,
  SheetSection,
  TextInputSheet,
} from '@/components/projects/MobileSheets';
import { AvatarStack, DueChip } from '@/components/projects/ProjectVisuals';
import { initialsOf, type PeoplePickerProfile } from '@/components/projects/PeoplePicker';
import {
  PRIORITY_META,
  PRIORITY_ORDER,
  STATUS_OPTION_ORDER,
  type TaskStatusOption,
  statusOptionMeta,
  taskStatusOption,
  taskStatusPatch,
} from '@/features/projects/display';
import {
  useCreateProjectSection,
  useCreateTask,
  useDeleteTask,
  useUpdateProjectSection,
  useUpdateTask,
  type ProjectSection,
  type TaskWithAssignees,
} from '@/features/projects/hooks';
import { formatWorkloadHours } from '@/features/projects/workload';
import { addDaysIsoDate, todayIsoDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { TaskPriority } from '@/types/database';

/**
 * Phone layout for a project's task list. Mirrors the desktop table's
 * grouping (unsectioned bucket first, then sections) but renders each
 * task as a card with large tap targets: a round completion toggle,
 * tappable priority / status chips that open bottom-sheet pickers, and
 * a kebab that opens a sheet with every structural move. Drag-and-drop
 * is desktop only — the sheet covers the same moves.
 */

export interface MobileTaskBucket {
  key: string;
  section: ProjectSection | null;
  tasks: TaskWithAssignees[];
  totalTaskCount: number;
}

type ProjectOption = { id: string; name: string };

export interface MobileTaskMoves {
  onMoveToSection: (task: TaskWithAssignees, sectionId: string | null) => void;
  onMakeSubtaskOf: (task: TaskWithAssignees, parentId: string) => void;
  onPromoteToMainTask: (task: TaskWithAssignees) => void;
  onMoveToProject: (task: TaskWithAssignees, projectId: string) => void;
  onAddToProject: (task: TaskWithAssignees, projectId: string) => void;
  onRemoveFromProject: (task: TaskWithAssignees) => void;
}

interface MobileTaskListProps {
  projectId: string;
  buckets: MobileTaskBucket[];
  linkedTasks: TaskWithAssignees[];
  subtasksByParent: Map<string, TaskWithAssignees[]>;
  visibleSubtasksByParent: Map<string, TaskWithAssignees[]>;
  expandedSubtasks: Set<string>;
  onToggleSubtasks: (parentId: string) => void;
  collapsedSections: Set<string>;
  onToggleSection: (sectionId: string) => void;
  sectionsList: ProjectSection[];
  allTasks: TaskWithAssignees[];
  members: PeoplePickerProfile[];
  otherProjects: ProjectOption[];
  disabled: boolean;
  isLoading: boolean;
  baseTotal: number;
  visibleTotal: number;
  onOpenTask: (task: TaskWithAssignees) => void;
  onMoveSection: (sectionId: string, direction: 'up' | 'down') => void;
  onDeleteSection: (section: ProjectSection) => void;
  moves: MobileTaskMoves;
}

export function MobileTaskList({
  projectId,
  buckets,
  linkedTasks,
  subtasksByParent,
  visibleSubtasksByParent,
  expandedSubtasks,
  onToggleSubtasks,
  collapsedSections,
  onToggleSection,
  sectionsList,
  allTasks,
  members,
  otherProjects,
  disabled,
  isLoading,
  baseTotal,
  visibleTotal,
  onOpenTask,
  onMoveSection,
  onDeleteSection,
  moves,
}: MobileTaskListProps) {
  const createSection = useCreateProjectSection();
  // `undefined` = sheet closed; `null` = open with no section preset.
  const [quickAddSection, setQuickAddSection] = useState<
    string | null | undefined
  >(undefined);
  const [addSectionOpen, setAddSectionOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  const cardContext = {
    projectId,
    members,
    disabled,
    sectionsList,
    allTasks,
    otherProjects,
    moves,
    onOpenTask,
  };

  const renderTask = (task: TaskWithAssignees) => {
    const allChildren = subtasksByParent.get(task.id) ?? [];
    const visibleChildren = visibleSubtasksByParent.get(task.id) ?? [];
    const expanded = expandedSubtasks.has(task.id);
    const isLinked = task.project_id !== projectId;
    return (
      <div key={task.id} className="space-y-2">
        <MobileTaskCard
          {...cardContext}
          task={task}
          subtaskCount={allChildren.length}
          expanded={expanded}
          onToggleSubtasks={() => onToggleSubtasks(task.id)}
        />
        {expanded && (visibleChildren.length > 0 || !disabled) && (
          <div className="ml-5 space-y-2 border-l-2 border-info-soft/70 pl-3">
            {visibleChildren.map((child) => (
              <MobileTaskCard
                key={child.id}
                {...cardContext}
                task={child}
                isSubtask
              />
            ))}
            {!disabled && !isLinked && (
              <SubtaskComposer projectId={projectId} parentTaskId={task.id} />
            )}
          </div>
        )}
      </div>
    );
  };

  const isEmptyProject = baseTotal === 0 && sectionsList.length === 0;

  return (
    <div className="space-y-5">
      {isEmptyProject ? (
        <EmptyState
          icon={ListTodo}
          title="No tasks yet"
          action={
            !disabled && (
              <Button onClick={() => setQuickAddSection(null)}>
                <Plus className="h-4 w-4" />
                Add task
              </Button>
            )
          }
        />
      ) : (
        visibleTotal === 0 &&
        baseTotal > 0 && (
          <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            No tasks match the current filters.
          </p>
        )
      )}

      {buckets.map((bucket) => {
        const section = bucket.section;
        if (!section) {
          if (bucket.tasks.length === 0 && linkedTasks.length === 0) return null;
          return (
            <div key={bucket.key} className="space-y-2">
              {bucket.tasks.map(renderTask)}
              {linkedTasks.map(renderTask)}
            </div>
          );
        }
        const collapsed = collapsedSections.has(section.id);
        const idx = sectionsList.findIndex((s) => s.id === section.id);
        return (
          <section key={bucket.key} className="space-y-2">
            <MobileSectionHeader
              section={section}
              count={bucket.totalTaskCount}
              collapsed={collapsed}
              onToggle={() => onToggleSection(section.id)}
              disabled={disabled}
              canMoveUp={idx > 0}
              canMoveDown={idx >= 0 && idx < sectionsList.length - 1}
              onMove={(dir) => onMoveSection(section.id, dir)}
              onDelete={() => onDeleteSection(section)}
            />
            {!collapsed && (
              <>
                {bucket.tasks.map(renderTask)}
                {bucket.tasks.length === 0 && disabled && (
                  <p className="px-1 text-xs italic text-muted-foreground">
                    No tasks in this section.
                  </p>
                )}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => setQuickAddSection(section.id)}
                    className="flex min-h-11 w-full items-center gap-2 rounded-lg border border-dashed border-border px-3 text-sm text-muted-foreground transition-colors active:bg-secondary"
                  >
                    <Plus className="h-4 w-4" />
                    Add task to {section.name}
                  </button>
                )}
              </>
            )}
          </section>
        );
      })}

      {!disabled && (
        <Button
          variant="ghost"
          className="h-11 w-full justify-start text-muted-foreground"
          onClick={() => setAddSectionOpen(true)}
        >
          <Plus className="h-4 w-4" />
          Add section
        </Button>
      )}

      {!disabled && (
        <button
          type="button"
          onClick={() => setQuickAddSection(null)}
          aria-label="Add task"
          className="fixed bottom-[max(env(safe-area-inset-bottom),1.25rem)] right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-bright-moss shadow-[0_8px_24px_-6px_hsl(124_26%_14%/0.45)] transition-transform active:scale-95"
        >
          <Plus className="h-6 w-6" strokeWidth={2.2} />
        </button>
      )}

      <QuickAddTaskSheet
        open={quickAddSection !== undefined}
        onOpenChange={(open) => {
          if (!open) setQuickAddSection(undefined);
        }}
        projectId={projectId}
        initialSectionId={quickAddSection ?? null}
        sectionsList={sectionsList}
        members={members}
      />

      <TextInputSheet
        open={addSectionOpen}
        onOpenChange={setAddSectionOpen}
        title="New section"
        placeholder="e.g. Design"
        confirmLabel="Add section"
        pending={createSection.isPending}
        onSubmit={async (name) => {
          try {
            await createSection.mutateAsync({ projectId, name });
            setAddSectionOpen(false);
          } catch (err) {
            toast.error(
              err instanceof Error ? err.message : 'Could not add section',
            );
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function MobileSectionHeader({
  section,
  count,
  collapsed,
  onToggle,
  disabled,
  canMoveUp,
  canMoveDown,
  onMove,
  onDelete,
}: {
  section: ProjectSection;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  disabled: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (dir: 'up' | 'down') => void;
  onDelete: () => void;
}) {
  const updateSection = useUpdateProjectSection();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  return (
    <div className="flex items-center gap-1 pt-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg pr-2 text-left"
      >
        <span aria-hidden className="h-5 w-1 shrink-0 rounded-full bg-info" />
        <span className="truncate text-xs font-medium uppercase tracking-wider text-info">
          {section.name}
        </span>
        <Badge variant="info-soft" className="shrink-0 px-2 py-0 tabular-nums">
          {count}
        </Badge>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            collapsed && '-rotate-90',
          )}
        />
      </button>
      {!disabled && (
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label={`Actions for section ${section.name}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground active:bg-secondary"
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      )}

      <ActionSheet
        open={menuOpen}
        onOpenChange={setMenuOpen}
        title={section.name}
        description="Section"
      >
        <ActionSheetItem
          icon={Pencil}
          label="Rename section"
          onSelect={() => {
            setMenuOpen(false);
            setRenameOpen(true);
          }}
        />
        <ActionSheetItem
          icon={ArrowUp}
          label="Move up"
          disabled={!canMoveUp}
          onSelect={() => {
            setMenuOpen(false);
            onMove('up');
          }}
        />
        <ActionSheetItem
          icon={ArrowDown}
          label="Move down"
          disabled={!canMoveDown}
          onSelect={() => {
            setMenuOpen(false);
            onMove('down');
          }}
        />
        <ActionSheetItem
          icon={Trash2}
          label="Delete section"
          destructive
          onSelect={() => {
            setMenuOpen(false);
            onDelete();
          }}
        />
      </ActionSheet>

      <TextInputSheet
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Rename section"
        initialValue={section.name}
        pending={updateSection.isPending}
        onSubmit={(name) => {
          if (name === section.name) {
            setRenameOpen(false);
            return;
          }
          updateSection.mutate(
            { id: section.id, projectId: section.project_id, patch: { name } },
            {
              onSuccess: () => setRenameOpen(false),
              onError: (err) =>
                toast.error(err instanceof Error ? err.message : 'Rename failed'),
            },
          );
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task card
// ---------------------------------------------------------------------------

interface CardContext {
  projectId: string;
  members: PeoplePickerProfile[];
  disabled: boolean;
  sectionsList: ProjectSection[];
  allTasks: TaskWithAssignees[];
  otherProjects: ProjectOption[];
  moves: MobileTaskMoves;
  onOpenTask: (task: TaskWithAssignees) => void;
}

function MobileTaskCard({
  task,
  isSubtask = false,
  subtaskCount = 0,
  expanded = false,
  onToggleSubtasks,
  projectId,
  disabled,
  sectionsList,
  allTasks,
  otherProjects,
  moves,
  onOpenTask,
}: CardContext & {
  task: TaskWithAssignees;
  isSubtask?: boolean;
  subtaskCount?: number;
  expanded?: boolean;
  onToggleSubtasks?: () => void;
}) {
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const [prioritySheet, setPrioritySheet] = useState(false);
  const [statusSheet, setStatusSheet] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  const isLinked = task.project_id !== projectId;
  const priority = PRIORITY_META[task.priority];
  const statusOption = taskStatusOption(task);
  const status = statusOptionMeta(statusOption);
  const workload = formatWorkloadHours(task.approximate_workload_hours);

  const persist = (patch: Parameters<typeof updateTask.mutate>[0]['patch']) => {
    updateTask.mutate(
      { id: task.id, projectId: task.project_id, patch },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Update failed'),
      },
    );
  };

  const onDelete = () => {
    const label = isSubtask ? 'subtask' : 'task';
    const tail =
      subtaskCount > 0
        ? ` This will also delete ${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}.`
        : '';
    if (!window.confirm(`Delete ${label} “${task.name}”?${tail}`)) return;
    deleteTask.mutate(
      { id: task.id, projectId: task.project_id },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Delete failed'),
      },
    );
  };

  const open = () => onOpenTask(task);
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open details for ${task.name}`}
        onClick={open}
        onKeyDown={(e: KeyboardEvent) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault();
            open();
          }
        }}
        className={cn(
          'relative grid grid-cols-[2.75rem_minmax(0,1fr)_2.75rem] items-start gap-x-1 overflow-hidden rounded-lg border border-border bg-card py-1.5 pl-1.5 pr-1 text-left transition-colors active:bg-secondary/40',
          task.is_completed && 'bg-secondary/30',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0 left-0 w-1',
            task.is_completed ? 'bg-bright-moss-dark/40' : priority.dot,
          )}
        />
        <button
          type="button"
          onClick={(e) => {
            stop(e);
            persist({ is_completed: !task.is_completed });
          }}
          disabled={disabled}
          role="checkbox"
          aria-checked={task.is_completed}
          aria-label={
            task.is_completed ? 'Mark as not completed' : 'Mark as completed'
          }
          className="row-span-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-full disabled:opacity-50"
        >
          <span
            className={cn(
              'flex items-center justify-center rounded-full border-[1.5px] transition-colors',
              isSubtask ? 'h-5 w-5' : 'h-[22px] w-[22px]',
              task.is_completed
                ? 'border-bright-moss-dark bg-bright-moss-dark text-linen'
                : 'border-old-growth/50 bg-card',
            )}
          >
            {task.is_completed && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
          </span>
        </button>

        <p
          className={cn(
            'col-start-2 break-words pb-2 pt-2.5 leading-snug text-foreground',
            isSubtask ? 'text-[13px]' : 'text-sm',
            disabled && 'col-span-2 pr-2',
            task.is_completed && 'text-muted-foreground line-through',
          )}
        >
          {task.name}
        </p>

        {!disabled && (
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              setActionsOpen(true);
            }}
            aria-label={`Actions for ${task.name}`}
            className="-mb-2 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground active:bg-secondary"
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
        )}

        <div className="col-span-2 col-start-2 flex flex-wrap items-center gap-1.5 pb-2.5 pr-2">
          <DueChip due={task.due_date} completed={task.is_completed} />
          <ChipButton
            className={priority.className}
            disabled={disabled}
            label={`Priority: ${priority.label}`}
            onClick={(e) => {
              stop(e);
              setPrioritySheet(true);
            }}
          >
            {priority.label}
          </ChipButton>
          <ChipButton
            className={status.className}
            disabled={disabled}
            label={`Status: ${status.label}`}
            onClick={(e) => {
              stop(e);
              setStatusSheet(true);
            }}
          >
            {status.label}
          </ChipButton>
          {workload && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" aria-hidden />
              {workload}
            </span>
          )}
        </div>

        {(task.assignees.length > 0 ||
          subtaskCount > 0 ||
          (isLinked && !isSubtask)) && (
          <div className="col-span-2 col-start-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 pb-2.5 pr-2">
            {task.assignees.length > 0 && (
              <AvatarStack people={task.assignees} max={3} />
            )}
            {isLinked && !isSubtask && (
              <span
                className="inline-flex max-w-full items-center gap-1 truncate rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                title={`Lives in “${task.project?.name ?? 'another project'}”`}
              >
                <FolderInput className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  from {task.project?.name ?? 'another project'}
                </span>
              </span>
            )}
            {subtaskCount > 0 && onToggleSubtasks && (
              <button
                type="button"
                onClick={(e) => {
                  stop(e);
                  onToggleSubtasks();
                }}
                aria-expanded={expanded}
                className="relative -my-1 inline-flex min-h-8 items-center gap-1 rounded-full px-1 text-xs font-medium text-info after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-['']"
              >
                <ListTree className="h-3.5 w-3.5" />
                {subtaskCount} subtask{subtaskCount === 1 ? '' : 's'}
                <ChevronRight
                  className={cn(
                    'h-3.5 w-3.5 transition-transform',
                    expanded && 'rotate-90',
                  )}
                />
              </button>
            )}
          </div>
        )}
      </div>

      <OptionSheet<TaskPriority>
        open={prioritySheet}
        onOpenChange={setPrioritySheet}
        title="Priority"
        value={task.priority}
        options={PRIORITY_ORDER.map((p) => ({
          value: p,
          label: <ChipLabel className={PRIORITY_META[p].className}>{PRIORITY_META[p].label}</ChipLabel>,
        }))}
        onChange={(p) => persist({ priority: p })}
      />
      <OptionSheet<TaskStatusOption>
        open={statusSheet}
        onOpenChange={setStatusSheet}
        title="Status"
        value={statusOption}
        options={STATUS_OPTION_ORDER.map((s) => ({
          value: s,
          label: (
            <ChipLabel className={statusOptionMeta(s).className}>
              {statusOptionMeta(s).label}
            </ChipLabel>
          ),
        }))}
        onChange={(s) => persist(taskStatusPatch(task, s))}
      />
      <TaskActionSheet
        open={actionsOpen}
        onOpenChange={setActionsOpen}
        task={task}
        isSubtask={isSubtask}
        hasSubtasks={subtaskCount > 0}
        isLinked={isLinked}
        sectionsList={sectionsList}
        allTasks={allTasks}
        otherProjects={otherProjects}
        moves={moves}
        onOpen={open}
        onDelete={onDelete}
      />
    </>
  );
}

function ChipLabel({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Small chip that opens a picker. The visible pill stays compact; an
 * invisible pseudo-element pads the hit area to ~44px tall.
 */
function ChipButton({
  className,
  disabled,
  label,
  onClick,
  children,
}: {
  className: string;
  disabled: boolean;
  label: string;
  onClick: (e: MouseEvent) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "relative inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs after:absolute after:-inset-y-2.5 after:inset-x-0 after:content-[''] disabled:pointer-events-none",
        className,
      )}
    >
      {children}
      {!disabled && <ChevronDown className="h-3 w-3 opacity-60" aria-hidden />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Task actions sheet — mobile counterpart of the desktop kebab menu
// ---------------------------------------------------------------------------

type ActionView = 'root' | 'section' | 'parent' | 'move' | 'add';

function TaskActionSheet({
  open,
  onOpenChange,
  task,
  isSubtask,
  hasSubtasks,
  isLinked,
  sectionsList,
  allTasks,
  otherProjects,
  moves,
  onOpen,
  onDelete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: TaskWithAssignees;
  isSubtask: boolean;
  hasSubtasks: boolean;
  isLinked: boolean;
  sectionsList: ProjectSection[];
  allTasks: TaskWithAssignees[];
  otherProjects: ProjectOption[];
  moves: MobileTaskMoves;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const [view, setView] = useState<ActionView>('root');

  const setOpen = (next: boolean) => {
    if (!next) setView('root');
    onOpenChange(next);
  };
  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  // Same rules as the desktop menu: any other top-level task in the
  // task's home project can be a parent, and a task that already has
  // subtasks can't be nested (the DB caps nesting at one level).
  const candidateParents = allTasks.filter(
    (t) =>
      t.parent_task_id === null &&
      t.id !== task.id &&
      t.id !== task.parent_task_id &&
      t.project_id === task.project_id,
  );
  const showMakeSubtask = !hasSubtasks && candidateParents.length > 0;
  const currentSectionId = task.section_id ?? null;
  const back = (
    <ActionSheetItem icon={ChevronLeft} label="Back" onSelect={() => setView('root')} />
  );

  const titles: Record<ActionView, string> = {
    root: task.name,
    section: 'Move to section',
    parent: 'Make subtask of',
    move: 'Move to project',
    add: 'Add to project',
  };

  return (
    <ActionSheet open={open} onOpenChange={setOpen} title={titles[view]}>
      {view === 'root' && (
        <>
          <ActionSheetItem
            icon={PanelRight}
            label="Open details"
            onSelect={() => run(onOpen)}
          />
          {isLinked ? (
            <>
              {!isSubtask && (
                <ActionSheetItem
                  icon={Unlink}
                  label="Remove from this project"
                  onSelect={() => run(() => moves.onRemoveFromProject(task))}
                />
              )}
              <ActionSheetItem
                icon={Trash2}
                label={`Delete ${isSubtask ? 'subtask' : 'task'} everywhere`}
                destructive
                onSelect={() => run(onDelete)}
              />
            </>
          ) : (
            <>
              <ActionSheetItem
                icon={ListTodo}
                label="Move to section"
                drillDown
                onSelect={() => setView('section')}
              />
              {showMakeSubtask && (
                <ActionSheetItem
                  icon={ListTree}
                  label="Make subtask of"
                  drillDown
                  onSelect={() => setView('parent')}
                />
              )}
              {isSubtask && (
                <ActionSheetItem
                  icon={CornerLeftUp}
                  label="Promote to main task"
                  onSelect={() => run(() => moves.onPromoteToMainTask(task))}
                />
              )}
              <ActionSheetItem
                icon={FolderInput}
                label="Move to project"
                drillDown
                onSelect={() => setView('move')}
              />
              {!isSubtask && (
                <ActionSheetItem
                  icon={FolderPlus}
                  label="Add to project"
                  drillDown
                  onSelect={() => setView('add')}
                />
              )}
              <ActionSheetItem
                icon={Trash2}
                label={`Delete ${isSubtask ? 'subtask' : 'task'}`}
                destructive
                onSelect={() => run(onDelete)}
              />
            </>
          )}
        </>
      )}

      {view === 'section' && (
        <>
          {back}
          <ActionSheetItem
            label="No section"
            selected={!isSubtask && currentSectionId === null}
            onSelect={() => run(() => moves.onMoveToSection(task, null))}
          />
          {sectionsList.map((s) => (
            <ActionSheetItem
              key={s.id}
              label={s.name}
              selected={!isSubtask && currentSectionId === s.id}
              onSelect={() => run(() => moves.onMoveToSection(task, s.id))}
            />
          ))}
        </>
      )}

      {view === 'parent' && (
        <>
          {back}
          {candidateParents.map((p) => (
            <ActionSheetItem
              key={p.id}
              label={p.name}
              onSelect={() => run(() => moves.onMakeSubtaskOf(task, p.id))}
            />
          ))}
        </>
      )}

      {(view === 'move' || view === 'add') && (
        <>
          {back}
          {otherProjects.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              No other projects.
            </p>
          ) : (
            otherProjects.map((p) => (
              <ActionSheetItem
                key={p.id}
                label={p.name}
                onSelect={() =>
                  run(() =>
                    view === 'move'
                      ? moves.onMoveToProject(task, p.id)
                      : moves.onAddToProject(task, p.id),
                  )
                }
              />
            ))
          )}
        </>
      )}
    </ActionSheet>
  );
}

// ---------------------------------------------------------------------------
// Inline subtask composer
// ---------------------------------------------------------------------------

function SubtaskComposer({
  projectId,
  parentTaskId,
}: {
  projectId: string;
  parentTaskId: string;
}) {
  const createTask = useCreateTask();
  const [name, setName] = useState('');

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || createTask.isPending) return;
    createTask.mutate(
      { projectId, name: trimmed, parentTaskId },
      {
        onSuccess: () => setName(''),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not add subtask'),
      },
    );
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="flex items-center gap-2"
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Add subtask…"
        enterKeyHint="done"
        className="h-11 flex-1 border-dashed text-base sm:text-sm"
      />
      <Button
        type="submit"
        variant="secondary"
        className="h-11"
        disabled={!name.trim() || createTask.isPending}
      >
        Add
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Quick-add sheet
// ---------------------------------------------------------------------------

function QuickAddTaskSheet({
  open,
  onOpenChange,
  projectId,
  initialSectionId,
  sectionsList,
  members,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  initialSectionId: string | null;
  sectionsList: ProjectSection[];
  members: PeoplePickerProfile[];
}) {
  const createTask = useCreateTask();
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [sectionId, setSectionId] = useState<string | null>(initialSectionId);
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);

  // Reset the draft each time the sheet opens so a cancelled task
  // doesn't linger, and pick up the section the sheet was opened from.
  useEffect(() => {
    if (!open) return;
    setName('');
    setDueDate('');
    setPriority('medium');
    setSectionId(initialSectionId);
    setAssigneeIds([]);
  }, [open, initialSectionId]);

  const today = todayIsoDate();
  const dueShortcuts = [
    { label: 'Today', value: today },
    { label: 'Tomorrow', value: addDaysIsoDate(today, 1) },
    { label: 'Next week', value: addDaysIsoDate(today, 7) },
  ];

  const toggleAssignee = (id: string) =>
    setAssigneeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || createTask.isPending) return;
    createTask.mutate(
      {
        projectId,
        name: trimmed,
        assigneeIds,
        dueDate: dueDate || undefined,
        priority,
        sectionId,
      },
      {
        onSuccess: () => {
          toast.success('Task added');
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not add task'),
      },
    );
  };

  const sectionName = sectionsList.find((s) => s.id === sectionId)?.name;

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetHeader
        title="New task"
        description={sectionName ? `In ${sectionName}` : 'No section'}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="contents"
      >
        <BottomSheetBody className="space-y-5">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What needs to be done?"
            aria-label="Task name"
            autoFocus
            enterKeyHint="done"
            className="h-11 text-base"
          />

          <SheetSection title="Due date">
            <div className="flex flex-wrap gap-1.5">
              {dueShortcuts.map((s) => (
                <SheetChip
                  key={s.label}
                  active={dueDate === s.value}
                  onClick={() => setDueDate(dueDate === s.value ? '' : s.value)}
                >
                  {s.label}
                </SheetChip>
              ))}
            </div>
            <DatePicker
              value={dueDate}
              onChange={setDueDate}
              aria-label="Due date"
              showShortcuts={false}
              className="h-11 text-base"
            />
          </SheetSection>

          <SheetSection title="Priority">
            <div className="flex flex-wrap gap-1.5">
              {PRIORITY_ORDER.map((p) => (
                <SheetChip
                  key={p}
                  active={priority === p}
                  onClick={() => setPriority(p)}
                >
                  {priority !== p && (
                    <span
                      aria-hidden
                      className={cn('h-2 w-2 rounded-full', PRIORITY_META[p].dot)}
                    />
                  )}
                  {PRIORITY_META[p].label}
                </SheetChip>
              ))}
            </div>
          </SheetSection>

          {sectionsList.length > 0 && (
            <SheetSection title="Section">
              <div className="flex flex-wrap gap-1.5">
                <SheetChip
                  active={sectionId === null}
                  onClick={() => setSectionId(null)}
                >
                  No section
                </SheetChip>
                {sectionsList.map((s) => (
                  <SheetChip
                    key={s.id}
                    active={sectionId === s.id}
                    onClick={() => setSectionId(s.id)}
                  >
                    {s.name}
                  </SheetChip>
                ))}
              </div>
            </SheetSection>
          )}

          {members.length > 0 && (
            <SheetSection title="Assignees">
              <div className="flex flex-wrap gap-1.5">
                {members.map((m) => {
                  const active = assigneeIds.includes(m.id);
                  return (
                    <SheetChip
                      key={m.id}
                      active={active}
                      onClick={() => toggleAssignee(m.id)}
                      className="pl-1.5"
                    >
                      {!active && (
                        <Avatar className="h-6 w-6">
                          {m.avatar_url && <AvatarImage src={m.avatar_url} alt="" />}
                          <AvatarFallback className="text-[9px]">
                            {initialsOf(m)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {m.full_name ?? m.email}
                    </SheetChip>
                  );
                })}
              </div>
            </SheetSection>
          )}
        </BottomSheetBody>
        <BottomSheetFooter>
          <Button
            type="button"
            variant="ghost"
            className="h-11 w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-11 w-full sm:w-auto"
            disabled={!name.trim() || createTask.isPending}
          >
            {createTask.isPending ? 'Adding…' : 'Add task'}
          </Button>
        </BottomSheetFooter>
      </form>
    </BottomSheet>
  );
}
